// Construction du menu à imprimer (modèles Canva de Loïc) à partir des lignes du devis.

export const MODELS = {
  buffet: { label: "Buffet", ornament: "plate", separator: true },
  cocktail: { label: "Cocktail", ornament: "brush", separator: false },
  repas: { label: "Autres formats", ornament: "plate", separator: true },
};

export const guessModel = (order) => {
  // Le format (tag « buffets froids », « cocktail déjeunatoire ou dînatoire »…)
  // prime : prestation_type vaut « plateaux repas » pour un buffet froid.
  const type = `${order?.format || ""} ${order?.prestation_type_fr || ""}`.toLowerCase();
  if (type.includes("cocktail")) return "cocktail";
  if (type.includes("buffet")) return "buffet";
  return "repas";
};

// Gamme du forfait (« Le Buffet Signature - Mixte » → « Signature »)
const guessRange = (order) => {
  for (const article of order?.articles || []) {
    const match = (article.name || "").match(/buffet\s+([^\s-]+)/i);
    if (match) return match[1];
  }
  return "";
};

// Titre selon le format de la prestation (tag : « buffets froids »,
// « cocktail apéritif », « petit déjeuner »…), fidèle aux modèles Canva.
export const defaultTitle = (model, order) => {
  const format = (order?.format || "").trim();
  if (model === "cocktail") {
    return { light: "Votre menu", bold: /ap[ée]ritif/i.test(format) ? "Cocktail apéritif" : "Cocktail" };
  }
  if (model === "buffet") {
    const range = guessRange(order);
    const hot = /chaud/i.test(format) ? " chaud" : "";
    return { light: `Votre buffet${hot}${range ? `\n${range}` : ""}`, bold: "À partager" };
  }
  return { light: "Votre menu", bold: otherFormatTitle(format) };
};

// Autres formats : titre générique, y compris pour les formats d'appels d'offres
// (« Préfecture AURA 2023 - Accueil petit déjeuner » → « Petit déjeuner »).
const OTHER_TITLES = [
  [/petit[\s-]*d[ée]jeuner/i, "Petit déjeuner"],
  [/go[uû]ter/i, "Goûter"],
  [/pause|collation/i, "Pause gourmande"],
  [/plateau/i, "Plateau repas"],
  [/nomade|pique[\s-]*nique/i, "Repas nomade"],
  [/animation/i, "Animation culinaire"],
  [/d[ée]jeuner|d[îi]ner|repas/i, "À table"],
];

const otherFormatTitle = (format) => {
  const match = OTHER_TITLES.find(([pattern]) => pattern.test(format));
  if (match) return match[1];
  // Format inconnu : dernier segment du nom, sans les numéros de lot ni l'année
  const last = (format.split(" - ").pop() || "").replace(/\b\d+(\.\d+)*\b/g, "").trim();
  return last ? last.charAt(0).toUpperCase() + last.slice(1) : "À table";
};

// Nom de production → libellé de menu :
// « Salade Classique Carné - Petits pois, radis… » → « Petits pois, radis… »
// « Tarte (8 parts) » → « Tarte »
const cleanName = (name) => {
  let label = (name || "").replace(/\s*\(\d+\s*parts?\)\s*$/i, "").replace(/\s+/g, " ").trim();
  // Seul le préfixe court « Salade Classique Carné - » est retiré, pas le
  // nom d'une vraie salade (« Salade de pépinette…, haddock - Sauce basilic »).
  const dash = label.match(/^salade (?:classique|signature)[^,-]*-\s*(.+)$/i);
  if (dash) label = dash[1];
  return label.charAt(0).toUpperCase() + label.slice(1);
};

// Beaucoup de plats n'ont ni salé ni sucré coché (fromages des formules,
// crumble, clafoutis, salade de fruits…) : on se rabat alors sur la catégorie.
const SWEET_CATEGORY = /dessert|p[aâ]tisserie|viennoiserie/i;
const SALTED_CATEGORY = /machon|salade|sandwich|quiche|gratin|plat|planche/i;

const course = (line) => {
  if (line.sweet) return "sweet";
  if (line.salted) return "salted";
  if (SWEET_CATEGORY.test(line.category || "")) return "sweet";
  if (SALTED_CATEGORY.test(line.category || "")) return "salted";
  return null; // boissons, matériel, service
};

// Texte affiché en fin de ligne à partir de l'API (fiches recettes Koust).
const allergenText = (info) => {
  if (!info) return "";
  if (info.status === "ok") return info.allergens.join(", ");
  if (info.status === "empty") return "allergènes non renseignés";
  return "fiche recette manquante";
};

// BO : rapprochement produit ↔ fiche recette Koust, filtré sur le nom du produit.
const recipeLinkUrl = (name) =>
  `${process.env.REACT_APP_API_URL}/e28XmZh3/rapprochementproduct/list?filter%5Bname%5D%5Bvalue%5D=${encodeURIComponent(name).replace(/%20/g, "+")}`;

// Plats salés puis desserts : [{ text, allergens, warning, recipeUrl }]
// allergenMap = réponse de /api/pro/orders/{id}/menu-allergens (par id de ligne).
export const extractDishes = (order, allergenMap = {}) => {
  const salted = [];
  const sweet = [];
  const push = (line) => {
    const text = cleanName(line.product_part_name || line.renamed_name || line.name);
    const list = { salted, sweet }[course(line)];
    if (!text || !list || list.some((dish) => dish.text === text)) return;
    const info = allergenMap?.[line.id];
    list.push({
      text,
      allergens: allergenText(info),
      warning: Boolean(info) && info.status !== "ok",
      recipeUrl: info?.status === "missing" ? recipeLinkUrl(line.name || text) : null,
    });
  };
  for (const article of order?.articles || []) {
    if (article.children?.length) article.children.forEach(push);
    else push(article);
  }
  return { salted, sweet };
};

// Allergènes numérotés : légende en bas de page, numéros à côté des plats.
// Ordre réglementaire des 14 allergènes (règlement INCO) ; libellé inconnu en fin.
const ALLERGEN_ORDER = [
  "gluten", "crustacés", "œuf", "poisson", "arachide", "soja", "lait", "fruits à coque",
  "céleri", "moutarde", "sésame", "sulfites", "lupin", "mollusques",
];

export const splitAllergens = (text) =>
  (text || "").split(",").map((a) => a.trim().toLowerCase()).filter(Boolean);

// Allergènes présents sur les plats (hors alertes « fiche manquante »), dans l'ordre réglementaire.
export const buildAllergenLegend = (dishes) => {
  const present = new Set();
  dishes.filter((d) => !d.warning).forEach((d) => splitAllergens(d.allergens).forEach((a) => present.add(a)));
  const rank = (a) => (ALLERGEN_ORDER.includes(a) ? ALLERGEN_ORDER.indexOf(a) : ALLERGEN_ORDER.length);
  return [...present].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
};

// « œuf, lait » → « 2, 3 » selon la légende
export const allergenNumbers = (text, legend) =>
  splitAllergens(text).map((a) => legend.indexOf(a) + 1).filter((n) => n > 0).sort((a, b) => a - b).join(", ");

// Saisie à la main sur un plat : numéros de la légende ou noms d'allergènes → noms
export const parseAllergenInput = (input, legend) =>
  splitAllergens((input || "").replace(/[()]/g, ""))
    .map((token) => (/^\d+$/.test(token) ? legend[parseInt(token, 10) - 1] : token))
    .filter(Boolean)
    .join(", ");
